import { RootState } from '../../redux/reducers';
import {
  deleteAdminInvites,
  setAdminInvites,
  setAdmins,
} from '../../redux/slices/adminSlice';
import {
  addCommunity,
  setOnboarding,
} from '../../redux/slices/applicationSlice';
import { store } from '../../redux/store';
import Community, { Admin, AdminInvite, CommunityRole } from '../../types/community';
import authService from '../authService';
import httpService from '../httpService';
import { AdminInviteData, CreateAdminInviteRequest } from '../../models/adminService.model';
import { BusinessAdminRole } from '../../types/business';

const businessAdminService = {
  sendAdminInvites: async (emails: string[], role: CommunityRole) => {
    const body: CreateAdminInviteRequest = {
      emails,
      role,
    };
    const { selectedBusiness } = (store.getState() as RootState).application;
    return httpService.post<AdminInvite[]>(`/networks/businesses/${selectedBusiness}/admins/invites`, body).then(({ data }) => {
      const adminInvites = (store.getState() as RootState).admins.invites;
      store.dispatch(setAdminInvites([...data, ...adminInvites]));
      return data;
    });
  },

  getAdminInvite: async (token: string): Promise<AdminInviteData> => {
    const { selectedBusiness } = (store.getState() as RootState).application;
    return httpService
      .get<AdminInviteData>(`/networks/businesses/${selectedBusiness}/admins/invites/${token}`)
      .then((res) => res.data);
  },

  //TODO: Community model should be changed to business model
  acceptInvite: async (token: string, inviteId: string): Promise<void> => {
    const { selectedBusiness } = (store.getState() as RootState).application;

    return httpService
      .post<Community>(`/networks/businesses/${selectedBusiness}/admins/invites/${inviteId}/accept`, { token })
      .then(({ data }): void => {
        store.dispatch(addCommunity(data));
      });
  },

  registerAndAccept: async (token: string, email: string, alias: string, inviteId: string): Promise<void> => {
    await authService.register(alias, email);
    await businessAdminService.acceptInvite(token, inviteId);
  },

  requestDifferentEmail: async (token: string, inviteId: string): Promise<void> => {
    const { selectedBusiness } = (store.getState() as RootState).application;
    return httpService.post(`/networks/businesses/${selectedBusiness}/admins/invites/${inviteId}/different-email`, { token });
  },

  getAllAdmins: (): Promise<Admin[]> => {
    const { selectedBusiness } = (store.getState() as RootState).application;
    return httpService.get<Admin[]>(`/networks/businesses/${selectedBusiness}/admins/`).then(({ data }) => {
      store.dispatch(setAdmins(data));
      return data;
    });
  },

  getAllAdminInvites: (): Promise<AdminInvite[]> => {
    const { selectedBusiness } = (store.getState() as RootState).application;
    return httpService.get<AdminInvite[]>(`/networks/businesses/${selectedBusiness}/admins/invites`).then(({ data }) => {
      store.dispatch(setAdminInvites(data));
      return data;
    });
  },

  deleteAdmins: async (userIds: number[]): Promise<void> => {
    const { selectedBusiness } = (store.getState() as RootState).application;
    await Promise.all(
      userIds.map((userId) =>
        httpService.delete(`/networks/businesses/${selectedBusiness}/admins/${userId}`),
      ),
    );
  },

  deleteAdminInvites: (inviteIds: number[]): Promise<void> => {
    const { selectedBusiness } = (store.getState() as RootState).application;
    return httpService.delete(`/networks/businesses/${selectedBusiness}/admins/invites`, { data: { inviteIds } }).then(() => {
      store.dispatch(deleteAdminInvites(inviteIds));
    });
  },

  resendInviteLink: (inviteId: number): Promise<void> => {
    const { selectedBusiness } = (store.getState() as RootState).application;
    return httpService.post(`/networks/businesses/${selectedBusiness}/admins/invites/${inviteId}/resend`);
  },

  inviteAdminByUserIds: (userIds: number[], role: CommunityRole): Promise<void> => {
    const { selectedBusiness } = (store.getState() as RootState).application;
    const body: CreateAdminInviteRequest = {
      userIds,
      role,
    };
    return httpService
      .post<AdminInvite[]>(`/networks/businesses/${selectedBusiness}/admins/invites`, body)
      .then(({ data }) => {
        const adminInvites = (store.getState() as RootState).admins.invites;
        store.dispatch(setAdminInvites([...data, ...adminInvites]));
      });
  },

  makeAdminById: async (userIds: number[]): Promise<void> => {
    const { selectedBusiness } = (store.getState() as RootState).application;
    await Promise.all(
      userIds.map((userId) =>
        httpService.put(`/networks/businesses/${selectedBusiness}/admins/${userId}`, {
          role: BusinessAdminRole.MANAGER,
        }),
      ),
    );
    await businessAdminService.getAllAdmins();
  },

  setFinishedOnboarding: (): Promise<void> => {
    const { selectedBusiness } = (store.getState() as RootState).application;
    return httpService.patch(`/networks/businesses/${selectedBusiness}/admins/@me/onboarding`).then(() => {
      store.dispatch(setOnboarding(undefined));
    });
  },
};

export default businessAdminService;
